import { useCallback, useEffect, useRef, useState } from 'react'

import { Editor, useEditor } from '@craftjs/core'

import { useBuilderStore } from '@/components/builder/utils/store'
import { useDefaultTailwindConfig, useGlobalTailwindConfig, useMetadata } from '@/components/builder/utils/tailwindConfig/hooks'

import { PostProvider, usePostStore } from '../../../store'
import { BuilderStoreAdapter } from '../../BuilderForm'
import { BuilderProvider, useBuilder, useCraft, useEditing } from '../context'
import useAutoSelectEditing from '../hooks/useAutoSelectEditing'
import useCompiler from '../hooks/useCompiler'
import { PageBuilderStoreProvider } from '../store'

import { MESSAGE_SOURCE, parseError, postToParent } from './protocol'

const SETTLE_TIMEOUT = 700

export default function BulkPublishRunner() {
  const [currentId, setCurrentId] = useState(null)

  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== window.location.origin) return
      if (event.data?.source !== MESSAGE_SOURCE) return
      if (event.data?.type !== 'publish') return

      setCurrentId(event.data.id)
    }

    window.addEventListener('message', handleMessage)
    postToParent({ type: 'ready' })

    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const handleResult = useCallback((id, ok, error) => {
    postToParent({ type: 'result', id, ok, error })
  }, [])

  if (!currentId) {
    return null
  }

  return <PostProvider key={currentId}>
    <PageBuilderStoreProvider>
      <BuilderStoreAdapter>
        <PublishJob id={currentId} onResult={handleResult} />
      </BuilderStoreAdapter>
    </PageBuilderStoreProvider>
  </PostProvider>
}

function PublishJob({ id, onResult }) {
  const { collection: { fetchById: fetchPost } } = usePostStore()
  const { postBuilder: { fetchById: fetchBuilder } } = useBuilderStore()
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false

    !async function load() {
      try {
        const [, builder] = await Promise.all([fetchPost(id), fetchBuilder(id).catch(() => null)])
        if (cancelled) return

        if (builder && !builder.craft) {
          onResult(id, false, 'Builder content could not be loaded')
          return
        }

        setLoaded(true)
      } catch (error) {
        if (cancelled) return
        onResult(id, false, parseError(error))
      }
    }()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (!loaded) {
    return null
  }

  return <BuilderProvider>
    <PublishEditor id={id} onResult={onResult} />
  </BuilderProvider>
}

function PublishEditor({ id, onResult }) {
  const { resolver } = useCompiler()
  const { setValue: setCraft } = useCraft()

  const handleChange = useCallback(query => {
    setCraft(query.getSerializedNodes())
  }, [setCraft])

  return <Editor resolver={resolver} onNodesChange={handleChange} enabled={false}>
    <PublishTrigger id={id} onResult={onResult} />
  </Editor>
}

function PublishTrigger({ id, onResult }) {
  useAutoSelectEditing()
  useDefaultTailwindConfig()

  const { collection: { item: post } } = usePostStore()
  const { value: editing } = useEditing()
  const { value: craft } = useCraft()
  const { html, handlePublish } = useBuilder()
  const tailwindConfig = useGlobalTailwindConfig()
  const { value: meta } = useMetadata()

  const { hasRoot, base } = useEditor(({ nodes }) => ({
    hasRoot: !!nodes.ROOT,
    base: nodes.ROOT?.data?.custom?.tailwind?.base,
  }))

  const publishedRef = useRef(false)
  const latestRef = useRef({})
  latestRef.current = { editing, craft, handlePublish, tailwindConfig, meta }

  const entitiesLoaded = `${post?.id}` === `${id}`
  const editorHydrated = hasRoot && !!base && !!editing && !!craft?.ROOT
  const hydrated = !publishedRef.current && entitiesLoaded && editorHydrated && !!html

  useEffect(() => {
    if (!hydrated) return

    const timer = setTimeout(async () => {
      publishedRef.current = true

      try {
        const { editing, craft, handlePublish, tailwindConfig, meta } = latestRef.current
        editing.definition.craft = craft
        await handlePublish({ tailwindConfig, meta })
        onResult(id, true)
      } catch (error) {
        onResult(id, false, parseError(error))
      }
    }, SETTLE_TIMEOUT)

    return () => clearTimeout(timer)
  }, [hydrated, craft, html, tailwindConfig, meta, id, onResult])

  return null
}
